import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter, {
    isPasswordStrong,
    PASSWORD_NOT_STRONG_ENOUGH,
    passwordHasDisallowedWhitespace,
    PASSWORD_SPACE_NOT_ALLOWED,
    getPasswordPolicyViolationMessage,
} from '@/components/ui/PasswordStrengthMeter';

function ConfirmDeleteAdminPopup({ 
    titleText, 
    handleConfirmCreate, 
    CloseCreatPopup, 
    handleCondition, 
    closePopup,
    closeResendPopup,
    handleSubmit: onSubmitPassword,
    policyContext,
}) {
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleResetPassword = useCallback(() => {
        const newErrors = {};
        
        if (!password) {
            newErrors.password = 'New password is required';
        } else if (passwordHasDisallowedWhitespace(password)) {
            newErrors.password = PASSWORD_SPACE_NOT_ALLOWED;
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!isPasswordStrong(password, policyContext)) {
            newErrors.password =
                getPasswordPolicyViolationMessage(password, policyContext) || PASSWORD_NOT_STRONG_ENOUGH;
        }
        
        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm new password is required';
        } else if (confirmPassword.length < 8) {
            newErrors.confirmPassword = 'New Password and Confirm Password must match';
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'New Password and Confirm Password must match';
        }
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length === 0) {
            setLoading(true);
            onSubmitPassword?.([password, confirmPassword]);
        }
    }, [password, confirmPassword, onSubmitPassword, policyContext]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            if (titleText === "Click on confirm to add a dashboard") {
                CloseCreatPopup?.();
            } else if (titleText === "Are you sure want to delete this dashboard?") {
                closePopup?.();
            } else {
                closeResendPopup?.();
            }
        }
    }, [titleText, CloseCreatPopup, closePopup, closeResendPopup]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className={titleText === "Are you sure want to delete this dashboard?" ? 'sr-only' : ''}>
                        {titleText}
                    </DialogTitle>
                </DialogHeader>
                
                {/* Confirm Create Dashboard */}
                {titleText === "Click on confirm to add a dashboard" && (
                    <>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col items-center gap-6 py-4 text-center">
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="secondary" onClick={CloseCreatPopup}>No</Button>
                            <Button onClick={() => { setLoading(true); handleConfirmCreate?.(); }} disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Confirm
                            </Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Delete Dashboard Confirmation */}
                {titleText === "Are you sure want to delete this dashboard?" && (
                    <>
                        <div className="flex flex-col items-center text-center gap-4 mt-4 px-6 py-4">
                            <AlertTriangle className="h-12 w-12 text-amber-500" />
                            <p className="text-lg font-medium text-foreground">{titleText}</p>
                        </div>
                        <DialogFooter>
                            <Button variant="secondary" onClick={closePopup}>No</Button>
                            <Button variant="destructive" onClick={() => { setLoading(true); handleCondition?.(); }} disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Yes
                            </Button>
                        </DialogFooter>
                    </>
                )}
                
                {/* Change Password Section */}
                {titleText === "Change Password" && (
                    <>
                        <div className="flex flex-col gap-4">
                            <form id="change-password-admin-form" onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }} className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="password">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: '' })); }}
                                            placeholder="New Password"
                                            autoComplete="new-password"
                                            className={`pr-10${errors.password ? ' border-destructive' : ''}`}
                                        />
                                        <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <PasswordStrengthMeter password={password} policyContext={policyContext} />
                                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                                </div>
                                
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
                                            placeholder="Confirm New Password"
                                            autoComplete="new-password"
                                            className={`pr-10${errors.confirmPassword ? ' border-destructive' : ''}`}
                                        />
                                        <button type="button" tabIndex={-1} onClick={() => setShowConfirmPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                                </div>
                            </form>
                        </div>
                        <DialogFooter>
                            <Button type="submit" form="change-password-admin-form" disabled={loading}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={closeResendPopup}>
                                Cancel
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ConfirmDeleteAdminPopup;
